import { Link } from "@tanstack/react-router";
import {
  ArrowRight, AudioLines, BarChart3, CalendarClock, Check, ChevronRight, FileAudio, FileText,
  Headphones, Menu, Mic, Radio, Receipt, ShieldCheck, Siren, Smartphone,
  Sparkles, Square, Volume2, X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import logoUrl from "@/assets/mission-ready-logo.webp";
import livekitBadgeAsset from "@/assets/livekit-badge.webp.asset.json";
import { Button } from "@/components/ui/button";
import { trackFunnel } from "@/lib/funnel";
import { useSignedInHint } from "@/lib/public-session-hint";

import { PatrolTrailDemo } from "@/components/landing/PatrolTrailDemo";
import { SchedulingDemo } from "@/components/landing/SchedulingDemo";
import "@/components/landing/mission-ready-public.css";

const PRODUCT_LINKS = [
  { label: "Operations", to: "/security-guard-management-software" as const },
  { label: "Guard Software", to: "/security-guard-management-software" as const },
  { label: "MissionReady Voice DAR™", to: "/voice-dar" as const },
  { label: "Hands-Free Voice", to: "/hands-free" as const },
  { label: "Push-to-Talk Radio", to: "/radio-ptt" as const },
  { label: "Patrol", to: "/patrol" as const },
  { label: "Scheduling", to: "/scheduling" as const },
  { label: "Incident Reporting", to: "/incident-reporting" as const },
  { label: "Dispatch", to: "/dispatch" as const },
  { label: "Custom Reports", to: "/custom-reports" as const },
  { label: "Invoicing", to: "/invoicing-software" as const },
] as const;

const COMPANY_LINKS = [
  { label: "About", to: "/about" as const },
  { label: "Pricing", to: "/pricing" as const },
  { label: "FAQ", to: "/faq" as const },
  { label: "Contact", to: "/contact" as const },
] as const;

function trackCta(event: string, location: string) {
  try {
    trackFunnel("landing_cta_clicked", { cta_event: event, location });
  } catch {
    // Analytics must never interrupt navigation.
  }
}

export function PublicHeader() {
  // Network-free hint so a signed-in visitor never sees a bare "SIGN IN".
  const signedIn = useSignedInHint();
  const [open, setOpen] = useState(false);

  const closeRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const [dragX, setDragX] = useState(0);
  const dragRef = useRef(0);
  const swipe = useRef<{ x: number; y: number; axis: null | "h" | "v"; active: boolean }>({
    x: 0, y: 0, axis: null, active: false,
  });

  useEffect(() => {
    if (!open) return;
    // Lock background scroll. We only hide overflow — taking the page out of
    // flow (position: fixed) shifted the menu panel off screen while scrolled
    // and lost the visitor's place after navigating.
    const body = document.body;
    const prev = {
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "contain";
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const nodes = Array.from(
        panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = prev.overflow;
      body.style.overscrollBehavior = prev.overscrollBehavior;
      document.removeEventListener("keydown", onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  const onTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length !== 1) return;
    swipe.current = { x: event.touches[0].clientX, y: event.touches[0].clientY, axis: null, active: true };
    dragRef.current = 0;
    setDragX(0);
  };
  const onTouchMove = (event: React.TouchEvent) => {
    const s = swipe.current;
    if (!s.active) return;
    const dx = event.touches[0].clientX - s.x;
    const dy = event.touches[0].clientY - s.y;
    if (s.axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.axis = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
    }
    if (s.axis !== "h") return;
    dragRef.current = Math.max(0, dx);
    setDragX(dragRef.current);
  };
  const onTouchEnd = () => {
    const s = swipe.current;
    if (!s.active) return;
    s.active = false;
    s.axis = null;
    const shouldClose = dragRef.current > 80;
    dragRef.current = 0;
    setDragX(0);
    if (shouldClose) setOpen(false);
  };


  return (
    <header className="mr-public-header">
      <Link to="/" className="mr-wordmark" aria-label="MissionReady home">
        <img src={logoUrl} alt="" width={38} height={38} />
        <span><strong>MISSION</strong><em>READY</em></span>
      </Link>

      <nav className="mr-desktop-links" aria-label="Primary navigation">
        <Link to="/voice-dar">VOICE DAR™</Link>
        <Link to="/hands-free">HANDS-FREE VOICE</Link>
        <Link to="/security-guard-management-software">OPERATIONS</Link>
        <Link to="/patrol">PATROL</Link>
        <Link to="/scheduling">SCHEDULING</Link>
        <Link to="/incident-reporting">INCIDENTS</Link>
        <Link to="/pricing">PRICING</Link>
      </nav>

      <div className="mr-desktop-account">
        {signedIn ? (
          <Link to="/operations" className="mr-button mr-button-small">OPEN APP</Link>
        ) : (
          <>
            <Link to="/login">SIGN IN</Link>
            <Link to="/signup" className="mr-button mr-button-small" onClick={() => trackCta("nav_start_free", "header")}>START FREE</Link>
          </>
        )}
      </div>


      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="icon"
        className="mr-menu-trigger"
        aria-label="Open navigation menu"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="mr-mobile-menu"
        onClick={() => setOpen(true)}
      >
        <Menu aria-hidden="true" />
      </Button>

      {open && (
        <div className="mr-menu-overlay" role="presentation" onMouseDown={(event) => {
          if (event.currentTarget === event.target) setOpen(false);
        }}>
          <aside
            id="mr-mobile-menu"
            ref={panelRef}
            className="mr-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mr-mobile-menu-title"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            style={
              dragX > 0
                ? { transform: `translateX(${dragX}px)`, animation: "none", transition: "none" }
                : undefined
            }
          >
            <h2 id="mr-mobile-menu-title" className="mr-sr-only">Site navigation</h2>
            <div className="mr-menu-top">
              <span className="mr-kicker" aria-hidden="true"><i /> NAVIGATION // ACTIVE</span>
              <Button ref={closeRef} type="button" variant="outline" size="icon" aria-label="Close navigation menu" onClick={() => setOpen(false)}>
                <X aria-hidden="true" />
              </Button>
            </div>
            <div className="mr-menu-account">
              <p>ACCOUNT</p>
              {signedIn ? (
                <Link to="/operations" className="mr-button" onClick={() => setOpen(false)}>OPEN APP <ArrowRight /></Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)}>SIGN IN</Link>
                  <Link to="/signup" className="mr-button" onClick={() => { setOpen(false); trackCta("menu_start_free", "mobile_menu"); }}>START FREE <ArrowRight /></Link>
                </>
              )}
            </div>

            <MenuGroup title="Product" links={PRODUCT_LINKS} onNavigate={() => setOpen(false)} />
            <MenuGroup title="Compare" links={[{ label: "Compare MissionReady", to: "/compare" as const }]} onNavigate={() => setOpen(false)} />
            <MenuGroup title="Company" links={COMPANY_LINKS} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
    </header>
  );
}


function MenuGroup({ title, links, onNavigate }: {
  title: string;
  links: ReadonlyArray<{ label: string; to: typeof PRODUCT_LINKS[number]["to"] | "/compare" | typeof COMPANY_LINKS[number]["to"] }>;
  onNavigate: () => void;
}) {
  return (
    <div className="mr-menu-group">
      <p>{title}</p>
      {links.map((link) => (
        <Link key={link.to} to={link.to} onClick={onNavigate}>
          {link.label}<ChevronRight aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

// The current logo artwork, lighting, scanline, and drag physics are intentionally preserved.
function HoloLogo() {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const shineRef = useRef<HTMLDivElement | null>(null);
  const rotX = useRef(-8);
  const rotY = useRef(12);
  const velX = useRef(0);
  const velY = useRef(0.35);
  const dragging = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const lastBack = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      if (innerRef.current) innerRef.current.style.transform = `rotateX(${rotX.current}deg) rotateY(${rotY.current}deg)`;
      return;
    }
    let running = true;
    const tick = () => {
      if (!running) return;
      if (!dragging.current) {
        velX.current *= 0.96;
        velY.current = velY.current * 0.96 + 0.35 * 0.04;
        rotX.current += velX.current;
        rotY.current += velY.current;
        rotX.current *= 0.99;
      }
      if (innerRef.current) innerRef.current.style.transform = `rotateX(${rotX.current}deg) rotateY(${rotY.current}deg)`;
      const normY = ((rotY.current % 360) + 360) % 360;
      const isBack = normY > 90 && normY < 270;
      if (isBack !== lastBack.current && frontRef.current) {
        frontRef.current.style.borderColor = `rgba(204,0,0,${isBack ? 0.35 : 0.75})`;
        lastBack.current = isBack;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    const start = () => { if (rafRef.current === null) rafRef.current = requestAnimationFrame(tick); };
    const stop = () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
    const onVisibility = () => { document.hidden ? stop() : start(); };
    document.addEventListener("visibilitychange", onVisibility);
    start();
    return () => { running = false; stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  const point = (event: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in event && event.touches.length) return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    const mouse = event as React.MouseEvent;
    return { x: mouse.clientX, y: mouse.clientY };
  };
  const onStart = (event: React.MouseEvent | React.TouchEvent) => {
    dragging.current = true;
    lastPt.current = point(event);
    velX.current = 0;
    velY.current = 0;
  };
  const onMove = (event: React.MouseEvent | React.TouchEvent) => {
    const current = point(event);
    if (dragging.current) {
      const dx = current.x - lastPt.current.x;
      const dy = current.y - lastPt.current.y;
      rotY.current += dx * 0.65;
      rotX.current -= dy * 0.65;
      velY.current = dx * 0.55;
      velX.current = -dy * 0.55;
    }
    if (wrapRef.current && shineRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const sx = ((current.x - rect.left) / rect.width) * 100;
      const sy = ((current.y - rect.top) / rect.height) * 100;
      shineRef.current.style.background = `radial-gradient(circle at ${sx}% ${sy}%, rgba(255,255,255,.22), transparent 45%)`;
    }
    lastPt.current = current;
  };

  return (
    <div className="mr-holo-logo">
      <div className="mr-holo-halo" aria-hidden="true" />
      <div className="mr-holo-ground" aria-hidden="true" />
      <div ref={wrapRef} className="mr-holo-stage" onMouseDown={onStart} onMouseMove={onMove} onMouseUp={() => { dragging.current = false; }} onMouseLeave={() => { dragging.current = false; }} onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={() => { dragging.current = false; }} aria-label="Interactive MissionReady holographic logo">
        <div ref={innerRef} className="mr-holo-disc">
          <div ref={frontRef} className="mr-holo-face mr-holo-front">
            <img src={logoUrl} alt="MissionReady" draggable={false} width={480} height={480} fetchPriority="high" />
            <div ref={shineRef} className="mr-holo-shine" aria-hidden="true" />
            <div className="mr-holo-scan" aria-hidden="true" />
          </div>
          <div className="mr-holo-face mr-holo-back">MISSION<br />READY</div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, children, align = "left", level = 2 }: { eyebrow: string; children: ReactNode; align?: "left" | "center"; level?: 1 | 2 }) {
  const Heading = level === 1 ? "h1" : "h2";
  return <div className={`mr-section-heading ${align === "center" ? "is-centered" : ""}`}><p>{eyebrow}</p><Heading>{children}</Heading></div>;
}

const waveform = [13, 25, 38, 20, 46, 30, 54, 22, 42, 29, 48, 18, 34, 23, 15];

function VoiceDarConsole() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase(3);
      return;
    }
    const timer = window.setInterval(() => setPhase((value) => (value + 1) % 4), 2600);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="mr-voice-console" aria-label="MissionReady Voice DAR workflow demonstration">
      <div className="mr-console-status"><span><i /> FIELD UNIT // 07</span><span>SHIFT ACTIVE</span></div>
      <div className="mr-phone-screen">
        <div className={`mr-demo-panel ${phase === 0 ? "is-active" : ""}`}>
          <span className="mr-recording"><i /> RECORDING</span>
          <div className="mr-waveform" aria-hidden="true">{waveform.map((height, index) => <i key={index} style={{ height, animationDelay: `${index * 70}ms` }} />)}</div>
          <strong className="mr-timer">00:17</strong>
          <span className="mr-stop"><Square fill="currentColor" /> STOP</span>
        </div>
        <div className={`mr-demo-panel ${phase === 1 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><Radio /> TRANSCRIPTION</span>
          <p className="mr-transcript">“10:42 PM. Completed the north perimeter patrol. All access points secure and no abnormalities observed.”</p>
          <div className="mr-preserved-row"><span><FileAudio /> ORIGINAL AUDIO <b>✓ SAVED</b></span><span>TIMESTAMP <b>10:42 PM</b></span></div>
        </div>
        <div className={`mr-demo-panel ${phase === 2 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><Sparkles /> AI SUMMARY</span>
          <p className="mr-summary">Routine north perimeter patrol completed with no abnormalities observed.</p>
          <span className="mr-panel-label mr-notes-label"><FileText /> GUARD NOTES</span>
          <p className="mr-note">Delivery gate rechecked and secured.</p>
          <div className="mr-verified"><Check /> LOG VERIFIED</div>
        </div>
        <div className={`mr-demo-panel ${phase === 3 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><FileText /> DAR SHIFT SHEET</span>
          <div className="mr-timeline">
            {["10:12 PM — Patrol", "10:42 PM — Activity Log", "11:07 PM — Door Check", "11:36 PM — Vehicle Check"].map((entry, index) => <div key={entry} style={{ animationDelay: `${index * 100}ms` }}><i />{entry}</div>)}
          </div>
          <div className="mr-verified"><Check /> SHIFT SHEET UPDATED</div>
        </div>
      </div>
      <div className="mr-phase-rail" aria-hidden="true">
        {["SPEAK", "TRANSCRIBE", "VERIFY", "DOCUMENT"].map((label, index) => <span key={label} className={phase === index ? "is-active" : ""}>{label}</span>)}
      </div>
    </div>
  );
}

export function VoiceDarSection({ detailed = false }: { detailed?: boolean }) {
  return (
    <section className={`mr-voice-section ${detailed ? "is-detailed" : ""}`} id="voice-dar">
      <div className="mr-shell mr-voice-grid">
        <div className="mr-voice-copy">
          {!detailed && <span className="mr-live-chip"><i /> FLAGSHIP CAPABILITY</span>}
          <SectionHeading eyebrow={detailed ? "VERIFIABLE FIELD RECORD" : "MISSIONREADY VOICE DAR™"}>
            {detailed ? <>FROM SPOKEN OBSERVATION<br /><em>TO SHIFT-READY RECORD.</em></> : <>DOCUMENT THE SHIFT<br /><em>WITHOUT STOPPING THE SHIFT.</em></>}
          </SectionHeading>
          {!detailed && <div className="mr-process"><span>SPEAK</span><ArrowRight /><span>VERIFY</span><ArrowRight /><span>DOCUMENT</span></div>}
          <p>Officers record DAR logs by voice while they work. MissionReady transcribes the entry, preserves the original audio and timestamp, generates a concise summary, retains guard notes, and adds the verified log to the chronological shift sheet.</p>
          <div className="mr-proof-line"><Headphones /> DEEPGRAM-POWERED TRANSCRIPTION <span /> ORIGINAL AUDIO RETAINED</div>
          {!detailed && <Link to="/voice-dar" className="mr-text-link">SEE MISSIONREADY VOICE DAR™ IN ACTION <ArrowRight /></Link>}
        </div>
        <VoiceDarConsole />
      </div>
      {detailed && (
        <div className="mr-shell mr-record-grid">
          {[
            [FileAudio, "Original audio", "The source recording remains attached to the DAR record."],
            [Radio, "Full transcription", "Spoken activity becomes reviewable text for the officer to verify."],
            [ShieldCheck, "Human verified", "MissionReady assists documentation; the officer confirms the log."],
            [FileText, "Shift sheet ready", "Verified entries assemble in chronological order for submission."],
          ].map(([Icon, title, description]) => {
            const RecordIcon = Icon as typeof FileAudio;
            return <article key={title as string}><RecordIcon /><h3>{title as string}</h3><p>{description as string}</p></article>;
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Hands-Free Voice demonstration console.
 *
 * A controlled marketing walkthrough of the REAL MissionReady Voice flow —
 * wake phrase, capture, transcription, role-checked answer, spoken reply.
 * Every example shown maps to an intent the shipped assistant supports.
 */
function HandsFreeConsole() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase(2);
      return;
    }
    const timer = window.setInterval(() => setPhase((value) => (value + 1) % 4), 2800);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="mr-voice-console" aria-label="MissionReady Hands-Free Voice demonstration">
      <div className="mr-console-status">
        <span><i /> FIELD UNIT // 07</span><span>HANDS-FREE ON</span>
      </div>
      <div className="mr-phone-screen">
        <div className={`mr-demo-panel ${phase === 0 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><AudioLines /> WAKE PHRASE ENABLED</span>
          <p className="mr-transcript">“Hey MR…”</p>
          <div className="mr-preserved-row"><span>SETTINGS <b>MISSIONREADY VOICE</b></span><span>STATE <b>READY</b></span></div>
        </div>
        <div className={`mr-demo-panel ${phase === 1 ? "is-active" : ""}`}>
          <span className="mr-recording"><i /> LISTENING</span>
          <div className="mr-waveform" aria-hidden="true">
            {waveform.map((height, index) => <i key={index} style={{ height, animationDelay: `${index * 70}ms` }} />)}
          </div>
          <p className="mr-note">Officer speaks the request. Nothing is stored as a D.A.R. record.</p>
        </div>
        <div className={`mr-demo-panel ${phase === 2 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><Radio /> RECOGNIZED REQUEST</span>
          <p className="mr-transcript">“What’s my next stop?”</p>
          <div className="mr-preserved-row"><span>ROLE CHECK <b>✓ AUTHORIZED</b></span><span>SCOPE <b>ASSIGNED SITE</b></span></div>
        </div>
        <div className={`mr-demo-panel ${phase === 3 ? "is-active" : ""}`}>
          <span className="mr-panel-label"><Volume2 /> MISSIONREADY RESPONDS</span>
          <p className="mr-summary">Your next checkpoint is the North Dock entry. It’s the third stop on your active tour.</p>
          <div className="mr-verified"><Check /> SPOKEN ALOUD · SHOWN ON SCREEN</div>
        </div>
      </div>
      <div className="mr-phase-rail" aria-hidden="true">
        {["HEY MR", "LISTEN", "UNDERSTAND", "RESPOND"].map((label, index) => (
          <span key={label} className={phase === index ? "is-active" : ""}>{label}</span>
        ))}
      </div>
    </div>
  );
}

const HANDS_FREE_EXAMPLES = [
  "“Hey MR, what’s my next stop?”",
  "“Hey MR, what’s my shift status?”",
  "“Hey MR, who’s on site right now?”",
  "“Hey MR, what incidents were reported recently?”",
  "“Hey MR, start a DAR.”",
  "“Hey MR, I need to file an incident report.”",
];

export function HandsFreeSection() {
  return (
    <section className="mr-story-section mr-hands-free-story" id="hands-free">
      <div className="mr-shell mr-story-grid">
        <div className="mr-story-copy">
          <span className="mr-live-chip"><i /> MISSIONREADY VOICE</span>
          <SectionHeading eyebrow="HANDS-FREE VOICE">
            MEET MISSIONREADY<br /><em>HANDS-FREE VOICE.</em>
          </SectionHeading>
          <p className="mr-section-intro">Keep your hands and eyes on the job. Just say “Hey MR.”</p>
          <div className="mr-process"><span>HEY MR</span><ArrowRight /><span>LISTEN</span><ArrowRight /><span>RESPOND</span></div>
          <p>
            Authorized MissionReady users can work the platform by voice while they’re in the field.
            Say “Hey MR”, speak the request, and MissionReady transcribes it, answers within your own
            role and site permissions, shows the answer on screen, and speaks it back.
          </p>
          <div className="mr-hero-actions">
            <Link to="/hands-free" className="mr-button" onClick={() => trackCta("home_hands_free", "hands_free_section")}>
              SEE HANDS-FREE VOICE IN ACTION <ArrowRight />
            </Link>
          </div>
        </div>
        <HandsFreeConsole />
      </div>
    </section>
  );
}

export function HandsFreePage() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <section className="mr-product-hero">
          <div className="mr-grid-bg" aria-hidden="true" />
          <div className="mr-shell">
            <span className="mr-live-chip"><i /> MISSIONREADY VOICE</span>
            <h1>MISSIONREADY<br /><em>HANDS-FREE VOICE.</em></h1>
            <p>Your security operations system — without reaching for your phone.</p>
            <div className="mr-process"><span>HEY MR</span><ArrowRight /><span>LISTEN</span><ArrowRight /><span>UNDERSTAND</span><ArrowRight /><span>RESPOND</span><ArrowRight /><span>ACT</span></div>
          </div>
        </section>

        <HandsFreeSection />

        <section className="mr-capabilities">
          <div className="mr-shell">
            <SectionHeading eyebrow="HOW IT WORKS">A HANDS-FREE <em>OPERATIONAL ASSISTANT.</em></SectionHeading>
            <p className="mr-section-intro">
              Not a generic chatbot. MissionReady Voice works inside the same platform, the same data
              and the same permissions your team already uses.
            </p>
            <div className="mr-capability-grid">
              {[
                [AudioLines, "Listens for “Hey MR”", "When Hands-Free Voice is enabled, MissionReady listens for the wake phrase where the device browser supports it. A voice button is always available too."],
                [Mic, "Captures the request", "The spoken request is recorded for that moment only. Conversation audio is never turned into a D.A.R. record."],
                [Radio, "Transcribes it", "Deepgram transcription turns the request into text, and the recognized request is displayed on screen."],
                [ShieldCheck, "Checks your permissions", "Every request is answered inside the signed-in user’s existing role, organization and site assignments. Voice does not bypass permissions."],
                [Volume2, "Responds out loud and on screen", "MissionReady speaks a concise answer and shows the same response as text. Say “Stop” to cut the reply short."],
                [ArrowRight, "Hands you into the workflow", "Supported requests open the existing MissionReady screens — the D.A.R. recorder, the incident form, patrol navigation — after you confirm."],
              ].map(([Icon, title, copy]) => {
                const CapIcon = Icon as typeof AudioLines;
                return (
                  <article key={title as string}>
                    <CapIcon />
                    <h3>{title as string}</h3>
                    <p>{copy as string}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mr-voice-principle">
          <div className="mr-shell">
            <SectionHeading eyebrow="BUILT FOR THE FIELD">SECURITY OFFICERS SHOULDN’T HAVE TO <em>STARE AT THEIR PHONE.</em></SectionHeading>
            <p>
              Officers work with their hands, their eyes and their attention on the environment.
              Hands-Free Voice lets them ask MissionReady what they need and keep working. Less phone
              staring. More situational awareness.
            </p>
          </div>
        </section>

        <section className="mr-capabilities">
          <div className="mr-shell">
            <SectionHeading eyebrow="REAL REQUESTS">WHAT YOU CAN <em>ASK.</em></SectionHeading>
            <p className="mr-section-intro">
              Every example below maps to something MissionReady Voice actually supports today, and
              is answered only when your role allows it.
            </p>
            <div className="mr-capability-grid">
              {HANDS_FREE_EXAMPLES.map((example) => (
                <article key={example}><AudioLines /><h3 style={{ fontSize: 20 }}>{example}</h3></article>
              ))}
            </div>
            <div className="mr-product-cta">
              <h2>OPTIONAL. ALWAYS <em>YOUR CHOICE.</em></h2>
              <p>
                Hands-Free Voice is optional and off until it’s switched on. Turn it on or off any
                time in Settings → MissionReady Voice. Emergency alerts are never sent automatically —
                you confirm those yourself on the existing SOS screen.
              </p>
              <Link to="/signup" className="mr-button" onClick={() => trackCta("hands_free_start_free", "hands_free_page")}>START FREE <ArrowRight /></Link>
              <span><Check /> 30-day trial · No credit card</span>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
      <StickyMobileCta />
    </div>
  );
}

function PatrolStorySection() {
  return (
    <section className="mr-story-section mr-patrol-story">
      <div className="mr-shell mr-story-grid">
        <div className="mr-story-copy">
          <span className="mr-interactive-label"><i /> LIVE BREADCRUMB TRAIL</span>
          <SectionHeading eyebrow="LIVE PATROL">SEE THE OPERATION.<br /><em>LIVE.</em></SectionHeading>
          <div className="mr-process"><span>TRACK</span><ArrowRight /><span>VERIFY</span><ArrowRight /><span>PROVE</span></div>
          <p>MissionReady doesn't just tell you what happened. It creates the operational trail.</p>
          <Link to="/patrol" className="mr-text-link">EXPLORE LIVE PATROL <ArrowRight /></Link>
        </div>
        <PatrolTrailDemo />
      </div>
    </section>
  );
}

function SchedulingStorySection() {
  return (
    <section className="mr-story-section mr-scheduling-story">
      <div className="mr-shell mr-story-grid is-reversed">
        <div className="mr-story-copy">
          <span className="mr-interactive-label"><i /> DRAG TO ASSIGN</span>
          <SectionHeading eyebrow="SCHEDULING">BUILD THE SHIFT.</SectionHeading>
          <div className="mr-process"><span>DRAG</span><ArrowRight /><span>ASSIGN</span><ArrowRight /><span>DEPLOY</span></div>
          <p>Build schedules, assign officers, and organize coverage without wrestling with spreadsheets.</p>
          <Link to="/scheduling" className="mr-text-link">EXPLORE SCHEDULING <ArrowRight /></Link>
        </div>
        <SchedulingDemo />
      </div>
    </section>
  );
}

const platformCards = [
  { icon: Radio, title: "Operations", copy: "Command Center", to: "/security-guard-management-software" as const },
  { icon: Smartphone, title: "Guard Software", copy: "Mobile field operations", to: "/security-guard-management-software" as const },
  { icon: Mic, title: "Voice DAR™", copy: "Voice-first documentation", to: "/voice-dar" as const },
  { icon: AudioLines, title: "Hands-Free Voice", copy: "Say “Hey MR” in the field", to: "/hands-free" as const },
  { icon: ShieldCheck, title: "Patrol", copy: "GPS + signed QR verification", to: "/patrol" as const },
  { icon: CalendarClock, title: "Scheduling", copy: "Drag, assign & deploy", to: "/scheduling" as const },
  { icon: Siren, title: "Incidents", copy: "Structured field reporting", to: "/incident-reporting" as const },
  { icon: Radio, title: "Dispatch", copy: "Live response & communications", to: "/dispatch" as const },
  { icon: BarChart3, title: "Reports", copy: "Client-ready reporting", to: "/custom-reports" as const },
  { icon: Receipt, title: "Invoicing", copy: "Shift-to-invoice workflows", to: "/invoicing-software" as const },
];

function PlatformSection() {
  return (
    <section className="mr-platform">
      <div className="mr-shell">
        <SectionHeading eyebrow="ONE OPERATIONAL SYSTEM">THE MISSIONREADY <em>PLATFORM</em></SectionHeading>
        <p className="mr-section-intro">Run the operation, document the field, and deliver proof from one connected platform.</p>
        <div className="mr-platform-grid">{platformCards.map(({ icon: Icon, title, copy, to }) => <Link key={title} to={to}><Icon /><span><strong>{title}</strong><small>{copy}</small></span><ChevronRight /></Link>)}</div>
      </div>
    </section>
  );
}

export function PublicFooter() {
  return (
    <footer className="mr-public-footer">
      <div className="mr-shell mr-footer-grid">
        <div><Link to="/" className="mr-wordmark"><img src={logoUrl} alt="" width={38} height={38} /><span><strong>MISSION</strong><em>READY</em></span></Link><p>Security operations.<br />Proven in real time.</p></div>
        <FooterGroup title="Product" links={PRODUCT_LINKS} />
        <FooterGroup title="Company" links={COMPANY_LINKS} />
        <div className="mr-footer-group"><p>Account</p><Link to="/login">Sign In</Link><Link to="/signup">Start Free</Link><Link to="/compare">Compare MissionReady</Link></div>
        <div className="mr-footer-group"><p>Support</p><a href="mailto:support@missionready.dev">support@missionready.dev</a><Link to="/contact">Contact</Link><Link to="/faq">FAQ</Link></div>
      </div>
      <div className="mr-shell mr-footer-legal"><span>© {new Date().getFullYear()} MissionReady</span><span><Link to="/privacy-policy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/delete-account">Delete account</Link></span></div>
    </footer>
  );
}

function FooterGroup({ title, links }: { title: string; links: ReadonlyArray<{ label: string; to: typeof PRODUCT_LINKS[number]["to"] | typeof COMPANY_LINKS[number]["to"] }> }) {
  return <div className="mr-footer-group"><p>{title}</p>{links.map((link) => <Link key={link.to} to={link.to}>{link.label}</Link>)}</div>;
}

function StickyMobileCta() {
  return <aside className="mr-sticky-mobile" aria-label="Start your free trial"><Link to="/signup" className="mr-button" onClick={() => trackCta("mobile_sticky_start_free", "sticky_mobile")}>START FREE <ArrowRight /></Link><small>30-day trial • No credit card • Cancel anytime</small></aside>;
}

export function RadioSection({ detailed = false }: { detailed?: boolean }) {
  return (
    <section className={`mr-voice-section ${detailed ? "is-detailed" : ""}`} id="radio">
      <div className="mr-shell mr-voice-grid">
        <div className="mr-voice-copy">
          {!detailed && <span className="mr-live-chip"><i /> NEW</span>}
          <SectionHeading eyebrow="MISSIONREADY RADIO" level={detailed ? 1 : 2}>
            {detailed ? <>SITE PUSH-TO-TALK RADIO.<br /><em>HOLD TO TALK. LISTEN HANDS-FREE.</em></> : <>HOLD TO TALK.<br /><em>THE WHOLE SITE HEARS IT.</em></>}
          </SectionHeading>
          <p>Hold the Voice button to talk live to every officer on your site — no separate radio hardware. Each transmission is transcribed and saved as one searchable message in the site's Radio thread. Audio is never stored, and only people authorized for the site can join.</p>
          <div className="mr-proof-line">
            <Radio /> <a href="https://livekit.io/" target="_blank" rel="noopener noreferrer">AUDIO POWERED BY LIVEKIT</a>
            <span /> <a href="https://deepgram.com/" target="_blank" rel="noopener noreferrer">TRANSCRIPTION BY DEEPGRAM</a>
          </div>
          {!detailed && <Link to="/radio-ptt" className="mr-text-link">SEE MISSIONREADY RADIO <ArrowRight /></Link>}
        </div>
        <div className="mr-voice-console" aria-label="MissionReady Radio illustration">
          <div className="mr-console-status"><span><i /> SITE CHANNEL // NORTH GATE</span><span>3 ON CHANNEL</span></div>
          <div className="mr-phone-screen">
            <div className="mr-demo-panel is-active">
              <span className="mr-recording"><i /> TRANSMITTING</span>
              <div className="mr-waveform" aria-hidden="true">{waveform.map((height, index) => <i key={index} style={{ height, animationDelay: `${index * 70}ms` }} />)}</div>
              <p className="mr-transcript">“Unit 2, north gate is clear. Heading to the loading dock.”</p>
              <div className="mr-verified"><Check /> TRANSCRIPT SAVED TO RADIO THREAD</div>
            </div>
          </div>
          <div className="mr-phase-rail" aria-hidden="true">
            {["HOLD", "TALK", "RELEASE", "LOGGED"].map((label) => <span key={label}>{label}</span>)}
          </div>
        </div>
      </div>
      {detailed && (
        <div className="mr-shell mr-record-grid">
          {[
            [Radio, "One button", "Tap for Voice DAR™, hold for Radio — the same button officers already use."],
            [ShieldCheck, "Site-scoped", "Only officers authorized for a site can join or hear its channel."],
            [FileText, "Searchable log", "Every transmission becomes one transcript in the site's Radio thread."],
            [Headphones, "No audio stored", "Live audio is relayed by LiveKit and never recorded by MissionReady."],
          ].map(([Icon, title, description]) => {
            const RecordIcon = Icon as typeof FileAudio;
            return <article key={title as string}><RecordIcon /><h2 className="text-sm mt-[18px]">{title as string}</h2><p>{description as string}</p></article>;
          })}
        </div>
      )}
    </section>
  );
}

export function RadioFeaturePage() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <RadioSection detailed />
      </main>
      <PublicFooter />
    </div>
  );
}

export function MissionReadyHome() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <section className="mr-home-hero">
          <div className="mr-grid-bg" aria-hidden="true" />
          <div className="mr-shell mr-hero-shell">
            <div className="mr-hero-logo"><HoloLogo /><span>TOUCH // ROTATE</span></div>
            <div className="mr-hero-copy">
              <span className="mr-kicker"><i /> SECURITY OPERATIONS PLATFORM</span>
              <h1>SECURITY GUARD<br /><em>MANAGEMENT SOFTWARE.</em></h1>
              <p>One command center for private security companies — see every officer, patrol and incident as it happens, and give clients the proof without chasing paperwork.</p>
              <div className="mr-hero-trust" aria-label="Trust and technology attribution">
                <div className="mr-trust-item">
                  <span className="mr-trust-label">Featured</span>
                  <a
                    href="https://sitepatent.com/?utm_source=missionready.dev&utm_medium=badge"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label="Tracked on SitePatent (opens in new tab)"
                    className="mr-sitepatent-badge"
                  >
                    <img
                      src="https://sitepatent.com/api/badge?style=dark"
                      alt="Tracked on SitePatent"
                      height={54}
                      width={194}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </div>
                <div className="mr-trust-item">
                  <span className="mr-trust-label">Built With</span>
                  <div className="mr-trust-built">
                    <a
                      href="https://deepgram.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mr-deepgram-attribution"
                      aria-label="MissionReady Voice DAR transcription is powered by Deepgram (opens in new tab)"
                    >
                      <span className="mr-trust-icon" aria-hidden="true"><Headphones /></span>
                      <span className="mr-trust-text"><small>VOICE DAR TRANSCRIPTION</small><strong>POWERED BY DEEPGRAM</strong></span>
                    </a>
                    <a
                      href="https://livekit.io/?utm_source=missionready.dev&utm_medium=badge"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mr-livekit-attribution"
                      aria-label="MissionReady in-app site Radio audio is powered by LiveKit (opens in new tab)"
                    >
                      <span className="mr-trust-icon" aria-hidden="true"><img src={livekitBadgeAsset.url} alt="" width={40} height={38} loading="lazy" decoding="async" /></span>
                      <span className="mr-trust-text"><small>IN-APP SITE RADIO AUDIO</small><strong>POWERED BY LIVEKIT</strong></span>
                    </a>
                  </div>
                </div>
                <div className="mr-trust-item">
                  <span className="mr-trust-label">Recognized By</span>
                  <a
                    href="https://codetrendy.com/?utm_source=missionready.dev&utm_medium=badge"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="mr-codetrendy-badge"
                    aria-label="Check it out (opens in new tab)"
                  >
                    <img
                      src="https://codetrendy.com/api/badge?style=dark"
                      alt="Check it out"
                      height={54}
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <VoiceDarSection />
        <HandsFreeSection />
        <RadioSection />
        <PatrolStorySection />
        <SchedulingStorySection />
        <PlatformSection />
        <section className="mr-final-cta"><div className="mr-shell"><span className="mr-kicker"><i /> READY TO DEPLOY</span><h2>RUN THE SHIFT.<br /><em>PROVE THE WORK.</em></h2><p>MissionReady gives security teams one place to manage operations, document activity, and deliver accountability in real time.</p><div><Link to="/signup" className="mr-button" onClick={() => trackCta("final_start_free", "final_cta")}>START FREE <ArrowRight /></Link><Link to="/pricing" className="mr-button-secondary">VIEW PRICING</Link></div></div></section>
      </main>
      <PublicFooter />
      <StickyMobileCta />
    </div>
  );
}

export function VoiceDarPage() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <section className="mr-voice-page-hero"><div className="mr-grid-bg" aria-hidden="true" /><div className="mr-shell"><span className="mr-live-chip"><i /> MISSIONREADY VOICE DAR™</span><h1>DOCUMENT THE SHIFT<br /><em>WITHOUT STOPPING THE SHIFT.</em></h1><p>Voice-first DAR reporting built for officers who need to stay aware of the environment while creating a complete, verifiable field record.</p><div className="mr-process"><span>SPEAK</span><ArrowRight /><span>VERIFY</span><ArrowRight /><span>DOCUMENT</span></div></div></section>
        <VoiceDarSection detailed />
        <section className="mr-voice-principle"><div className="mr-shell"><SectionHeading eyebrow="DOCUMENTATION ASSISTANCE">THE OFFICER STAYS <em>IN CONTROL.</em></SectionHeading><p>MissionReady Voice DAR™ assists with documentation. It does not replace human judgment. Officers review the transcription, summary, category, and notes before verifying the entry for the shift sheet.</p><Link to="/signup" className="mr-button">START FREE <ArrowRight /></Link></div></section>
      </main>
      <PublicFooter />
      <StickyMobileCta />
    </div>
  );
}